import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ClipboardList, CheckCircle, Clock, LogOut, Plus } from 'lucide-react';
import api from '../api';
import toast from 'react-hot-toast';

const AgentDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    const user = localStorage.getItem('admin_user');
    if (!user) { navigate('/admin'); return; }
    const parsed = JSON.parse(user);
    if (parsed.role !== 'agent') { navigate('/admin/dashboard'); return; }
    setAgent(parsed);
    loadSessions(parsed.id);
  }, [navigate]);

  const loadSessions = async (agentId) => {
    try {
      const res = await api.get(`/survey/sessions?agent_id=${agentId}`);
      setSessions(res.data);
    } catch (err) {
      toast.error('Erreur de chargement');
    }
    setLoading(false);
  };

  const handleStartSurvey = () => {
    navigate('/enquete', { state: { agentId: agent.id } });
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/admin');
  };

  const completedCount = sessions.filter(s => s.is_completed).length;
  const pendingCount = sessions.filter(s => !s.is_completed).length;

  if (loading) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Chargement...</div>;
  }

  const isActive = (path) => location.pathname === path;

  return (
    <div style={{ minHeight: '100vh', background: '#f8faf8', display: 'flex' }}>
      {/* Sidebar */}
      <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: '240px', background: 'linear-gradient(180deg, #1B5E20, #2E7D32)', padding: '24px 16px', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px', padding: '0 8px' }}>
          <img src="/logo.webp" alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'white', padding: '2px' }} />
          <div>
            <div style={{ color: '#FFD54F', fontSize: '10px', fontWeight: '600', letterSpacing: '0.5px' }}>AGENT</div>
            <div style={{ color: 'white', fontSize: '13px', fontWeight: '700' }}>{agent?.full_name || agent?.username || 'Agent'}</div>
          </div>
        </div>

        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <button onClick={() => navigate('/agent')}
            style={{ background: isActive('/agent') ? 'rgba(255,255,255,0.15)' : 'transparent', border: 'none', color: 'white', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: isActive('/agent') ? '600' : '400', textAlign: 'left', width: '100%' }}>
            <ClipboardList size={18} /> Mes Enquêtes
          </button>
          <button onClick={handleStartSurvey}
            style={{ background: 'transparent', border: 'none', color: 'white', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: '400', textAlign: 'left', width: '100%' }}>
            <Plus size={18} /> Faire l'enquête
          </button>
        </nav>

        <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.7)', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
          <LogOut size={18} /> Déconnexion
        </button>
      </div>

      {/* Main Content */}
      <div style={{ marginLeft: '240px', flex: 1, padding: '24px' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', marginBottom: '24px' }}>Mes Enquêtes</h1>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <StatCard label="Total enquêtes" value={sessions.length} icon={<ClipboardList size={18} color="#2E7D32" />} />
            <StatCard label="En cours" value={pendingCount} icon={<Clock size={18} color="#FFA726" />} />
            <StatCard label="Terminées" value={completedCount} icon={<CheckCircle size={18} color="#66BB6A" />} />
          </div>

          {/* Start survey */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', marginBottom: '24px', border: '1px solid #e8e8e8', textAlign: 'center' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a', marginBottom: '8px' }}>Nouvelle enquête</h2>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>Cliquez ci-dessous pour démarrer une nouvelle enquête sur le terrain.</p>
            <button onClick={handleStartSurvey}
              style={{ background: 'linear-gradient(135deg, #2E7D32, #4CAF50)', color: 'white', border: 'none', padding: '14px 32px', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={20} /> Faire l'enquête
            </button>
          </div>

          {/* Sessions list */}
          <div style={{ background: 'white', borderRadius: '16px', padding: '20px', border: '1px solid #e8e8e8' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#1a1a1a', marginBottom: '16px' }}>Historique</h3>
            {sessions.length === 0 ? (
              <p style={{ color: '#888', fontSize: '13px', textAlign: 'center', padding: '20px' }}>Aucune enquête réalisée</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {sessions.map(s => (
                  <div key={s.id} style={{ padding: '12px', borderRadius: '10px', background: s.is_completed ? '#f0f8f0' : '#fafafa', border: `1px solid ${s.is_completed ? '#c8e6c9' : '#eee'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>Enquête #{s.id.slice(-6).toUpperCase()}</div>
                      <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>
                        {new Date(s.started_at).toLocaleDateString('fr-FR')} · {s.response_format === 'audio' ? 'Audio' : 'Texte'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', background: s.is_completed ? '#e8f5e9' : '#fff3e0', color: s.is_completed ? '#2E7D32' : '#FFA726', fontWeight: '600' }}>
                        {s.is_completed ? 'Terminée' : 'En cours'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function StatCard({ label, value, icon }) {
  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e8e8e8', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#f8faf8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a' }}>{value}</div>
        <div style={{ fontSize: '12px', color: '#888' }}>{label}</div>
      </div>
    </div>
  );
}

export default AgentDashboard;
